import { useEffect, useMemo } from 'react';
import type { DictaphoneButton, DictaphoneProduct } from './products/types';
import { WithApolloContext } from 'modules/Apollo/types';
import { identifyDictaphoneProducts } from './products';
import { useMicrophone } from 'domains/reporter/useMicrophone';
import { logger } from 'modules/logger';
import { useDictaphoneProductMetrics } from './useDictaphoneProductMetrics';
import { createDictaphoneMatchSignature } from './matchSignature';
import { toAudioInputFingerprint } from './usbDeviceLabel';

export type UseDictaphoneButtonsParams = WithApolloContext;

export type UseDictaphoneProductResult = {
  dictaphoneProduct: DictaphoneProduct | undefined;
  dictaphoneButtons: ReadonlyArray<DictaphoneButton>;
  audioInputDeviceLabel: string;
};

const loggedMatchSignatures = new Set<string>();

export const __resetLoggedMatchSignaturesForTests = () => {
  loggedMatchSignatures.clear();
};

export const useDictaphoneProduct = ({
  context,
}: UseDictaphoneButtonsParams): UseDictaphoneProductResult => {
  const { audioInputDeviceLabel, isLoaded } = useMicrophone({ context });

  const matchingProducts = useMemo(
    () => identifyDictaphoneProducts(toAudioInputFingerprint({ label: audioInputDeviceLabel })),
    [audioInputDeviceLabel]
  );

  const matchingProductCount = matchingProducts.size;
  const matchingProduct = matchingProducts.values().next().value;

  useDictaphoneProductMetrics({
    audioInputDeviceLabel,
    isMicrophoneLoaded: isLoaded,
    matchingProduct,
    matchingProducts,
  });

  useEffect(() => {
    if (!isLoaded && audioInputDeviceLabel === '') return;

    const matchingProductIds = Array.from(matchingProducts).map((product) => product.id);
    const selectedProductId = matchingProduct?.id;

    if (matchingProductCount === 0) {
      const matchSignature = createDictaphoneMatchSignature({
        deviceLabel: audioInputDeviceLabel,
        matchingProductIds,
        selectedProductId,
      });
      if (loggedMatchSignatures.has(matchSignature)) return;
      loggedMatchSignatures.add(matchSignature);
      logger.info('[useDictaphoneProduct] No dictaphone product matches', {
        deviceLabel: audioInputDeviceLabel,
        selectedProductId,
      });
      return;
    }

    if (matchingProductCount > 1) {
      const matchSignature = createDictaphoneMatchSignature({
        deviceLabel: audioInputDeviceLabel,
        matchingProductIds,
        selectedProductId,
      });
      if (loggedMatchSignatures.has(matchSignature)) return;
      loggedMatchSignatures.add(matchSignature);
      logger.info('[useDictaphoneProduct] Multiple dictaphone product matches', {
        deviceLabel: audioInputDeviceLabel,
        count: matchingProductCount,
        matchingProductIds,
        selectedProductId,
      });
    }
  }, [audioInputDeviceLabel, isLoaded, matchingProduct, matchingProductCount, matchingProducts]);

  return useMemo(
    () => ({
      dictaphoneProduct: matchingProduct,
      dictaphoneButtons: matchingProduct?.buttons ?? [],
      audioInputDeviceLabel,
    }),
    [audioInputDeviceLabel, matchingProduct]
  );
};
